import { AxiosInstance } from 'axios';
import { apiRoutes } from "@/shared/api/routes";
import { SortDirection } from '@/shared/components/DataTable';

export interface Filterset {
    id: number;
    name: string;
    target_model: string;
    enabled: boolean;
    shared: boolean;
    expression: string;
    user: {
        first_name: string;
        id: number;
        last_name: string;
        login: string;
        middle_initials: string;
    };
}

export interface GetFiltersetsParams {
    offset?: number;
    limit?: number;
    query?: (string | number | boolean | (string | number | boolean)[])[];
    sortKey?: string | null;
    sortDirection?: SortDirection;
}

export interface GetFiltersetsResult {
    filtersets: Filterset[];
    totalCount: number;
}

const SELECT_FIELDS = [
    "enabled",
    "expression",
    "id",
    "name",
    "shared",
    "target_model",
    "user.first_name",
    "user.id",
    "user.last_name",
    "user.login",
    "user.middle_initials",
];

const API_ENDPOINT = apiRoutes.proxy('support/internal/user_filter');

const mapRowToFilterset = (row: (string | number | null)[]): Filterset => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rowData: { [key: string]: any } = {};
    SELECT_FIELDS.forEach((field, index) => {
        const keys = field.split('.');
        const value = row[index];
        if (keys.length > 1) {
            if (!rowData[keys[0]]) rowData[keys[0]] = {};
            rowData[keys[0]][keys[1]] = value;
        } else {
            if (field === 'enabled' || field === 'shared') {
                rowData[field] = value === 'Yes' || value === 1;
            } else {
                rowData[field] = value;
            }
        }
    });
    return rowData as Filterset;
};

export const createFiltersetService = (apiClient: AxiosInstance) => {
    const getFiltersets = async (params: GetFiltersetsParams): Promise<GetFiltersetsResult> => {
        const { offset = 0, limit = 50, query = [], sortKey, sortDirection } = params;

        const defaultOrder: [string, string][] = [
            ["name", "asc_nulls_last"],
            ["target_model", "asc_nulls_last"],
            ["shared", "asc_nulls_last"],
            ["id", "asc"],
        ];

        // When a sort key is active: put the active column first with chosen direction,
        // then append the remaining default order entries (excluding duplicates of sortKey).
        const order: [string, string][] = sortKey
            ? [
                [sortKey, sortDirection === SortDirection.Descending ? "desc_nulls_last" : "asc_nulls_last"],
                ...defaultOrder.filter(([field]) => field !== sortKey),
              ]
            : defaultOrder;

        const response = await apiClient.post(`${API_ENDPOINT}/select_query`, {
            select: SELECT_FIELDS,
            options: {
                timeout: 10,
                offset,
                limit,
                order,
                calculate_total: true,
                return_results: true,
                select_distinct: true,
            },
            query,
        });

        const rawData: (string | number | null)[][] = response.data?.data ?? [];
        const totalCount: number = response.data?.info?.total_results ?? rawData.length;

        return {
            filtersets: rawData.map(mapRowToFilterset),
            totalCount,
        };
    };

    const getFiltersetById = (id: number) => {
        return apiClient.post(`${API_ENDPOINT}/select_query`, {
            select: SELECT_FIELDS,
            query: [["equal", "id", id]],
            options: {
                timeout: 10,
                offset: 0,
                limit: 1,
                calculate_total: false,
                return_results: true,
                select_distinct: true,
            },
        });
    };

    /**
     * Checks whether a filterset with the given name already exists for the
     * specified target_model and user. Returns true if a duplicate is found.
     */
    const checkFiltersetNameExists = async (
        name: string,
        targetModel: string,
        userId: number
    ): Promise<boolean> => {
        const response = await apiClient.post(`${API_ENDPOINT}/select_query`, {
            select: ['id'],
            query: [
                ['equal', 'name', name],
                ['and', ['equal', 'target_model', targetModel], ['equal', 'user.id', userId]],
            ],
            options: {
                timeout: 10,
                offset: 0,
                limit: 1,
                calculate_total: false,
                return_results: true,
                select_distinct: true,
            },
        });
        const rows: unknown[] = response?.data?.data ?? [];
        return rows.length > 0;
    };

    const createFilterset = (data: Partial<Filterset>) => {
        return apiClient.post(API_ENDPOINT, data);
    };

    const searchFiltersetsByName = async ({
        targetModel,
        searchTerm,
        limit = 8,
    }: {
        targetModel: string;
        searchTerm: string;
        limit?: number;
    }): Promise<Filterset[]> => {
        const trimmedSearchTerm = searchTerm.trim();

        if (!trimmedSearchTerm) {
            return [];
        }

        const result = await getFiltersets({
            limit,
            query: [
                ['equal', 'target_model', targetModel],
                ['equal', 'enabled', true],
                ['like', 'name', `%${trimmedSearchTerm}%`],
            ],
            sortKey: 'name',
            sortDirection: SortDirection.Ascending,
        });

        return result.filtersets;
    };

    const updateFilterset = (id: number, data: Partial<Filterset>) => {
        return apiClient.put(`${API_ENDPOINT}/${id}`, data);
    };

    const deleteFilterset = (id: number) => {
        return apiClient.delete(`${API_ENDPOINT}/${id}`);
    };

    return {
        getFiltersets,
        getFiltersetById,
        checkFiltersetNameExists,
        createFilterset,
        searchFiltersetsByName,
        updateFilterset,
        deleteFilterset,
    };
};
